import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Stack,
} from '@mui/material';
import {
  Form, RecordContextProvider, useNotify, useTranslate, useRefresh,
} from 'react-admin';
import { PointGeometry, SecurityInventoryItem } from '@x-guard/xgac-types/xgac';
import { GrayTextInputWithUpperLabel } from '../../../components/inputs/GrayTextInputWithUpperLabel';
import { GrayDateInputWithUpperLabel } from '../../../components/inputs/GrayDateInputWithUpperLabel';
import { GrayGoogleCoordinatesInputWithUpperLabel } from '../../../components/inputs/GrayGoogleCoordinatesInputWithUpperLabel';
import { xgacDataProvider } from '../../../../../dataProviders/xgacDataProvider';
import { securityItemsResource } from '../util';
import { GrayNumberInputWithUpperLabel } from '../../../components/inputs/GrayNumberInputWithUpperLabel';

export const EditItemModal = (props: {
  record: SecurityInventoryItem | null;
  open: boolean;
  onClose: () => void;
}) => {

  const record = props.record;

  const translate = useTranslate();
  const refresh = useRefresh();
  const notify = useNotify();

  if (!record) {

    return null;

  }

  const handleSubmit = async (values: Record<string, unknown>) => {

    const previousData = record;
    if (!previousData) {

      return;

    }

    const base = values.location as PointGeometry | null;
    const floor = (values as SecurityInventoryItem).location?.properties?.address?.floor;

    const data = {
      name: values.name,
      lastInspectionDate: values.lastInspectionDate,
      expiryDate: values.expiryDate,
      location: {
        ...base,
        properties: {
          ...base?.properties,
          address: {
            ...base?.properties?.address,
            ...(floor !== undefined && floor !== null ? { floor } : {}),
          },
        },
      },
    };
    try {

      await xgacDataProvider.update(securityItemsResource, {
        id: previousData._id,
        data,
        previousData,
      });
      refresh();
      props.onClose();

    } catch {

      notify('ra.notification.http_error', { type: 'warning' });

    }

  };

  return (
    // disablePortal in this case allows the location input to appear on top of the dialog and not be covered by the dialog
    <Dialog open={props.open} onClose={props.onClose} disablePortal
      sx={{
        '& .MuiDialog-paper': {
          width: '800px',
          maxWidth: '100%',
          maxHeight: '600px',
        },
      }}>
      <DialogTitle>{translate('resources.security-inventory-items.text.edit_item_title')}</DialogTitle>
      <DialogContent>
        <RecordContextProvider value={record}>
          <Form
            id="edit-item-form"
            record={record}
            onSubmit={handleSubmit}
          >
            <Stack spacing={0}>
              <GrayTextInputWithUpperLabel source="name" label="Name" />
              <GrayGoogleCoordinatesInputWithUpperLabel
                source="location"
                label={translate('general.text.location')}
                placeholder={translate('resources.buttons.text.building_dialog.0.input_label')} />
              <GrayDateInputWithUpperLabel
                source="lastInspectionDate" label={translate('resources.security-inventory-items.fields.last_inspection_date')} />
              <GrayDateInputWithUpperLabel source="expiryDate" label={translate('resources.security-inventory-items.fields.expiry_date')} />
              <GrayNumberInputWithUpperLabel source="location.properties.address.floor" label={translate('general.text.map_floors')} size="medium" />
            </Stack>
          </Form>
        </RecordContextProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>{translate('ra.action.cancel')}</Button>
        <Button type="submit" form="edit-item-form">{translate('ra.action.save')}</Button>
      </DialogActions>
    </Dialog>
  );

};
